/**
 * @file: The zoom of the time grid.
 *
 * A zoom step changes the length of one slot row. The row height stays at
 * SLOT_ROW_PX, so the same scroll offset shows a different time after the step.
 * The time of the top row is therefore read before the step and set again
 * after it. CalendarManager owns one instance. The toolbar and the bar hooks
 * call it through the manager.
 *
 * The level index in this class is the source of the slot options. The class
 * does not read the options back from FullCalendar.
 */

import { SLOT_ROW_PX } from '../bar-content'
import { readScroller } from '../grid-geometry'

/* global requestAnimationFrame cancelAnimationFrame */

/** The zoom levels, from the most zoomed out to the most zoomed in. */
const SLOT_LEVELS = Object.freeze([
  Object.freeze({ slotDuration: '02:00:00', slotLabelInterval: '02:00:00', minutes: 120 }),
  Object.freeze({ slotDuration: '01:00:00', slotLabelInterval: '01:00:00', minutes: 60 }),
  Object.freeze({ slotDuration: '00:30:00', slotLabelInterval: '01:00:00', minutes: 30 }),
  Object.freeze({ slotDuration: '00:15:00', slotLabelInterval: '00:30:00', minutes: 15 })
])

/** The calendar opens at one-hour rows. They reduce the vertical scroll. */
const START_LEVEL = 1

const STEPS = Object.freeze({ in: 1, out: -1 })

/** Give the start of the row that contains this time, at the given zoom level. */
function rowStart (minutes, level) {
  return Math.floor(minutes / level.minutes) * level.minutes
}

export class SlotZoom {
  constructor (calendarManager) {
    this.calendarManager = calendarManager
    this.controller = calendarManager.controller
    this.levelIndex = START_LEVEL
    // The step that waits for its frame, or null: { frame, topMinutes }.
    this.pendingStep = null
  }

  /** The slot options of the current level, for the calendar options. */
  slotOptions () {
    const { slotDuration, slotLabelInterval } = SLOT_LEVELS[this.levelIndex]

    return { slotDuration, slotLabelInterval }
  }

  /** How many minutes one slot row covers at the current zoom. */
  currentSlotMinutes () {
    return SLOT_LEVELS[this.levelIndex].minutes
  }

  /**
   * Change the slot duration one level, to zoom in or to zoom out.
   * @param {'in'|'out'} direction
   */
  cycleSlotDuration (direction) {
    const calendar = this.calendarManager.calendar
    const nextIndex = this.levelIndex + (STEPS[direction] || 0)

    if (!calendar || nextIndex === this.levelIndex || !SLOT_LEVELS[nextIndex]) {
      return
    }

    const topMinutes = this.anchorMinutes()
    this.cancelPendingStep()
    this.levelIndex = nextIndex

    const { slotDuration, slotLabelInterval } = this.slotOptions()
    calendar.setOption('slotDuration', slotDuration)
    calendar.setOption('slotLabelInterval', slotLabelInterval)

    // FullCalendar renders the new rows before the next frame. A scroll
    // request before that frame uses the positions of the old rows.
    const frame = requestAnimationFrame(() => this.restoreTop(topMinutes))
    this.pendingStep = { frame, topMinutes }

    this.controller._$log.info(`[ScheduleCalendar] Zoom ${direction}: slotDuration=${slotDuration}, topMinutes=${topMinutes}`)
  }

  /**
   * Give the time to put at the top after this step.
   *
   * Before the frame of a step runs, the scroll offset is still the offset of
   * the old rows. The rows now have a different length, so the offset shows
   * a wrong time. A second step therefore uses the time of the step before it.
   */
  anchorMinutes () {
    return this.pendingStep ? this.pendingStep.topMinutes : this.topRowMinutes()
  }

  /**
   * Give the start time of the top row, in minutes past midnight.
   * Give null when the grid has no scroller.
   */
  topRowMinutes () {
    const scroller = readScroller(this.calendarManager.calendar.el)

    if (!scroller) {
      return null
    }

    const rowIndex = Math.floor(scroller.scrollTop / SLOT_ROW_PX)

    return rowIndex * this.currentSlotMinutes()
  }

  /**
   * Scroll the new rows so that the row with the old top time is at the top.
   *
   * The calendar can go away before the frame runs, for example after a route
   * change. The frame therefore tests the calendar again.
   */
  restoreTop (topMinutes) {
    this.pendingStep = null

    if (!this.calendarManager.calendar) {
      return
    }

    if (topMinutes !== null) {
      const minutes = rowStart(topMinutes, SLOT_LEVELS[this.levelIndex])
      this.calendarManager.calendar.scrollToTime({ minutes })
    }

    // Each row now covers a different length of time, so both surfaces hold
    // positions of the old grid. A slotDuration change starts neither datesSet
    // nor eventsSet, and the calendar element keeps its size. This code
    // therefore asks for the redraw.
    this.calendarManager.renderOnScreenSurfaces()
  }

  /** Cancel the frame of the step that waits. A newer step replaces it. */
  cancelPendingStep () {
    if (this.pendingStep) {
      cancelAnimationFrame(this.pendingStep.frame)
      this.pendingStep = null
    }
  }

  /** The calendar goes away, so the frame of a step has no grid to scroll. */
  destroy () {
    this.cancelPendingStep()
  }
}
